import React from 'react';

import { MenuReorderWidget } from 'features/menu/components/menu-reorder-widget';
import { MenuSection } from 'features/menu/components/menu-section/menu-section';
import { MenuSkeletonLoader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-loading';
import withMenuEmptyState from 'features/menu/components/with-menu-empty-state';
import { useMenuScroll } from 'features/menu/hooks/use-menu-scroll';
import { MenuEmptyState } from 'features/menu/pages/main-menu-page/menu-empty-state';
import { useStoreContext } from 'features/store';
import { View } from 'react-native';

import { useMaybePlayAudioOnceOnMount } from 'components/app-takeover/play-audio-once';
import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import { Helmet } from 'components/helmet';
import { LayoutContainer } from 'components/layout/styled';
import { usePullToReload } from 'components/pull-to-reload/pull-to-reload';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { useMenuContext } from 'state/menu';
import { useServiceModeContext } from 'state/service-mode';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { waitForDuration } from 'utils/wait-for-duration';

// NEW one menu
const MainMenuPage = () => {
  useRumPageView('main-menu', 'Main Menu');
  useMenuScroll();

  const {
    mainMenu,
    isLoading: isMenuLoading,
    refetchMenu,
    rawMenu,
    hasError,
    loadedStoreNumber,
  } = useUnifiedMenuContext();
  const { activeDayParts, showBurgersForBreakfast } = useMenuContext();
  const { serviceMode } = useServiceModeContext();

  const isMobile = useIsMobileBreakpoint();
  const { store } = useStoreContext();
  const isCurrentStoreMenuLoaded = Boolean(
    store?.number && loadedStoreNumber === store.number && rawMenu
  );

  const isLoading = isMenuLoading || (!hasError && !isCurrentStoreMenuLoaded);

  const onRefresh = async () => {
    // This is just an animation feedback of one second
    await waitForDuration(1000);
    refetchMenu();
  };

  const pullToReloadProps = usePullToReload({
    onRefresh,
    isDisabled: isLoading,
  });

  useMaybePlayAudioOnceOnMount(useAppTakeover()?.menu?.audio, {
    shouldPlay: !isLoading && mainMenu?.length > 0,
    globalPlayOnce: true,
  });

  if (isLoading) {
    return <MenuSkeletonLoader />;
  }

  return (
    <LayoutContainer isFullContainer={isMobile}>
      <Helmet title={t('mainMenu')} />
      <View style={{ flex: 1 }}>
        <MenuReorderWidget />
        {mainMenu.length > 0 ? (
          <MenuSection data={mainMenu} {...pullToReloadProps} />
        ) : (
          <MenuEmptyState
            onRefresh={onRefresh}
            logWhenTrue={!isLoading}
            logContext={{
              menuType: '1m',
              activeDayParts: activeDayParts.map(dayPart => dayPart.key),
              showBurgersForBreakfast,
              serviceMode,
              rawMenuLength: rawMenu?.entries?.length,
              isFetchedMenuEmpty: !isCurrentStoreMenuLoaded || (rawMenu?.entries.length ?? 0) === 0,
              isCurrentStoreMenuLoaded,
              isPricingAndDependenciesSettled: true,
              storeId: store?.number,
              storePosVendor: store?.pos?.vendor,
            }}
          />
        )}
      </View>
    </LayoutContainer>
  );
};

// MainMenuPage.whyDidYouRender = true;
export const MainMenuPageV2 = withMenuEmptyState(MainMenuPage);
